import {
  normalizeNavigationTabStyle,
  type NavigationTabStyle,
} from "./navigation-tab-style.mjs";
import {
  matchesAttributeFilters,
  type AttributeFilter,
} from "./navigation-attributes.mjs";
import { Capacitor, CapacitorHttp } from "@capacitor/core";
import type { VideoItem, EpisodeItem } from "./data";
export const IS_DEMO =
  import.meta.env.DEV &&
  typeof location !== "undefined" &&
  new URLSearchParams(location.search).get("demo") === "1";
const WEB_BASE = import.meta.env.VITE_API_BASE_URL || "/api/v1";
const NATIVE_BASE =
  import.meta.env.VITE_NATIVE_API_BASE_URL || "https://ytapp.bbccys.com/api/v1";
export interface RemoteVideo {
  id: string;
  title?: string;
  name?: string;
  contentType?: string;
  type?: string;
  portrait?: string;
  landscape?: string;
  description?: string;
  actors?: string;
  year?: string;
  area?: string;
  episodeCount?: number;
  views?: string | number;
  playCount?: string | number;
  creator?: string;
  source?: { collectorName?: string };
  playlist?: string;
  episodes?: {
    id?: string;
    episode?: number;
    title?: string;
    url?: string;
    status?: string;
  }[];
  properties?: string[];
  categories?: string[];
  category?: string;
}
export interface FeedResponse {
  items: RemoteVideo[];
  total: number;
  nextCursor: string | null;
  hasMore: boolean;
}
export interface NavigationItem {
  id: string;
  name: string;
  icon: string;
  targetType: "categories" | "keywords" | "external_url";
  categories: { id: string; name: string }[];
  attributeFilters?: AttributeFilter[];
  allCategories?: boolean;
  keywords: string[];
  externalUrl: string;
}
export interface CustomNavigation {
  tabStyle?: NavigationTabStyle;
  id: string;
  name: string;
  displayTitle?: string;
  items: NavigationItem[];
}
const CUSTOM_NAVIGATION =
  import.meta.env.VITE_H5_NAVIGATION_ID || "NAV-MUZBC6Y4";
export async function getAppTemplate(signal?: AbortSignal) {
  const code =
    new URLSearchParams(location.search).get("template") ||
    import.meta.env.VITE_APP_TEMPLATE_CODE ||
    "default";
  return getJSON(
    `/content/navigation?template=${encodeURIComponent(code)}&renderer=1`,
    signal,
  ) as Promise<{
    template: unknown;
    items?: CustomNavigation[];
    components?: unknown;
    componentRenderers?: unknown;
    bottomMenu?: unknown;
  }>;
}

export async function getCustomNavigation(
  signal?: AbortSignal,
): Promise<CustomNavigation> {
  return getNavigationGroup(CUSTOM_NAVIGATION, signal);
}
export async function getNavigationGroup(
  id: string,
  signal?: AbortSignal,
): Promise<CustomNavigation> {
  const body = (await getJSON(
    `/content/navigation?navigation=${encodeURIComponent(id)}&renderer=1`,
    signal,
  )) as {
    items?: Array<CustomNavigation & { status?: string }>;
  };
  const group = body.items?.find(
    (item) => item.id === id && item.status !== "停用",
  );
  if (!group || !Array.isArray(group.items))
    throw new ApiError("指定导航未启用或尚未配置");
  return {
    id: group.id,
    name: group.name,
    tabStyle: normalizeNavigationTabStyle(group.tabStyle),
    displayTitle:
      typeof group.displayTitle === "string" ? group.displayTitle.trim() : "",
    items: group.items
      .filter(
        (item) =>
          item &&
          typeof item.id === "string" &&
          typeof item.name === "string" &&
          (item as NavigationItem & { status?: string }).status !== "停用" &&
          ["categories", "keywords", "external_url"].includes(item.targetType),
      )
      .map((item) => ({
        ...item,
        icon: typeof item.icon === "string" ? item.icon : "",
        categories: Array.isArray(item.categories)
          ? item.categories.filter((c) => typeof c?.name === "string")
          : [],
        keywords: Array.isArray(item.keywords)
          ? item.keywords.filter((k) => typeof k === "string" && k.trim())
          : [],
        externalUrl:
          typeof item.externalUrl === "string" ? item.externalUrl : "",
      })),
  };
}
export function filterNavigationVideos(
  items: VideoItem[],
  menu: NavigationItem,
) {
  return items.filter((video) => {
    if (menu.targetType === "categories")
      return (
        (menu.allCategories === true ||
          menu.categories.some(
            (c) => video.labels?.includes(c.name) || video.category === c.name,
          )) &&
        matchesAttributeFilters(video, menu.attributeFilters)
      );
    if (menu.targetType === "keywords") {
      const text =
        `${video.title} ${video.description} ${video.collection} ${video.actors || ""}`.toLowerCase();
      return menu.keywords.some((word) =>
        text.includes(word.trim().toLowerCase()),
      );
    }
    return false;
  });
}
export class ApiError extends Error {
  constructor(
    message: string,
    public status?: number,
  ) {
    super(message);
    this.name = "ApiError";
  }
}
export function mediaUrl(value?: string) {
  if (!value) return "";
  try {
    const url = new URL(value, "https://ytapp.bbccys.com");
    return url.protocol === "https:" ? url.href : "";
  } catch {
    return "";
  }
}
async function getJSON(path: string, signal?: AbortSignal): Promise<unknown> {
  if (signal?.aborted) throw new DOMException("Aborted", "AbortError");
  if (Capacitor.isNativePlatform()) {
    const response = await CapacitorHttp.get({
      url: NATIVE_BASE.replace(/\/$/, "") + path,
      headers: { Accept: "application/json" },
      connectTimeout: 12000,
      readTimeout: 12000,
    });
    if (signal?.aborted) throw new DOMException("Aborted", "AbortError");
    if (response.status < 200 || response.status >= 300)
      throw new ApiError(`接口读取失败（${response.status}）`, response.status);
    if (response.status === 204) return null;
    return typeof response.data === "string"
      ? JSON.parse(response.data)
      : response.data;
  }
  const controller = new AbortController();
  const abort = () => controller.abort();
  signal?.addEventListener("abort", abort);
  const timer = window.setTimeout(abort, 12000);
  try {
    const response = await fetch(WEB_BASE.replace(/\/$/, "") + path, {
      signal: controller.signal,
      headers: { Accept: "application/json" },
      credentials: "omit",
    });
    if (!response.ok)
      throw new ApiError(`接口读取失败（${response.status}）`, response.status);
    return response.status === 204 ? null : await response.json();
  } catch (error) {
    if (signal?.aborted) throw error;
    if (controller.signal.aborted) throw new ApiError("接口请求超时，请重试");
    if (error instanceof ApiError) throw error;
    throw new ApiError("暂时无法连接视频服务，请检查网络");
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener("abort", abort);
  }
}
export async function getFeed(
  cursor = "",
  signal?: AbortSignal,
): Promise<FeedResponse> {
  const body = (await getJSON(
    `/content/feed?limit=18${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ""}`,
    signal,
  )) as FeedResponse;
  if (!body || !Array.isArray(body.items) || typeof body.total !== "number")
    throw new ApiError("视频列表格式不正确");
  return {
    ...body,
    items: body.items.filter((v) => v && typeof v.id === "string"),
    nextCursor: typeof body.nextCursor === "string" ? body.nextCursor : null,
    hasMore: !!body.hasMore,
  };
}
export async function getVideo(
  id: string,
  signal?: AbortSignal,
): Promise<RemoteVideo> {
  const body = (await getJSON(
    `/content/videos/${encodeURIComponent(id)}`,
    signal,
  )) as RemoteVideo;
  if (!body || typeof body.id !== "string")
    throw new ApiError("视频详情格式不正确");
  return body;
}
export function adaptVideo(remote: RemoteVideo): VideoItem {
  const cover = mediaUrl(remote.portrait || remote.landscape) || "/icon.svg";
  const episodes: EpisodeItem[] = (
    Array.isArray(remote.episodes) ? remote.episodes : []
  )
    .filter((e) => mediaUrl(e.url))
    .map((e, i) => ({
      id: e.id || `${remote.id}-${i + 1}`,
      number: Number(e.episode) || i + 1,
      title: e.title || `第${i + 1}集`,
      src: mediaUrl(e.url),
      cover,
      duration: "分集",
    }));
  if (!episodes.length && mediaUrl(remote.playlist))
    episodes.push({
      id: `${remote.id}-1`,
      number: 1,
      title: "正片",
      src: mediaUrl(remote.playlist),
      cover,
      duration: "正片",
    });
  return {
    id: remote.id,
    title: remote.title || remote.name || "未命名视频",
    description:
      remote.description && remote.description !== "暂无简介"
        ? remote.description
        : [remote.actors, remote.area, remote.year]
            .filter(Boolean)
            .join(" · ") || "暂无简介",
    creator: remote.creator || remote.source?.collectorName || "樱桃视频",
    category: remote.contentType || remote.type || "其他",
    cover,
    src: episodes[0]?.src,
    likes: 0,
    comments: 0,
    saves: 0,
    collection: remote.title || remote.name || "视频合集",
    episode: episodes[0]?.number || 1,
    duration: `${remote.episodeCount || episodes.length || 1}集`,
    remote: true,
    properties: Array.isArray(remote.properties) ? remote.properties : [],
    labels: Array.isArray(remote.categories)
      ? remote.categories
      : typeof remote.category === "string"
        ? remote.category.split(/\s*\/\s*/).filter((c) => c && c !== "未分类")
        : [],
    episodeCount: remote.episodeCount || episodes.length || 1,
    episodes,
    actors: remote.actors,
    detailLoaded: Array.isArray(remote.episodes) || !!remote.playlist,
  };
}
export async function getContentMenu(signal?: AbortSignal) {
  const body = (await getJSON("/content/menu", signal)) as {
    types?: string[];
    labels?: string[];
  };
  if (!Array.isArray(body.types) || !Array.isArray(body.labels))
    throw new ApiError("分类配置格式不正确");
  return {
    types: body.types.filter((v) => typeof v === "string"),
    labels: body.labels.filter((v) => typeof v === "string"),
  };
}

export interface ResolvedAdItem {
  ad: {
    id: string;
    name: string;
    description?: string;
    text?: string;
    materials?: { url: string }[];
  };
  plan: { id: string };
  destination: string;
  presentation?: { title?: { text: string }; description?: { text: string } };
}
export interface ResolvedAds {
  requestId: string;
  slot: { id: string };
  channel: string;
  items: ResolvedAdItem[];
}
export async function getComponentVideos(
  params: import("./template-platform.mjs").AppComponent["params"],
  signal?: AbortSignal,
): Promise<RemoteVideo[]> {
  const query = new URLSearchParams({ limit: String(params.limit) });
  if (params.sourceType) query.set("type", params.sourceType);
  if (params.category) query.set("category", params.category);
  if (params.keyword) query.set("keyword", params.keyword);
  if (params.contentIds.length) query.set("ids", params.contentIds.join(","));
  const result = (await getJSON(
    `/content/feed?${query}`,
    signal,
  )) as FeedResponse;
  return Array.isArray(result?.items) ? result.items : [];
}
export async function getComponentAds(
  slot: string,
  signal?: AbortSignal,
): Promise<ResolvedAds | null> {
  const terminal = Capacitor.isNativePlatform()
    ? Capacitor.getPlatform()
    : "h5";
  const query = new URLSearchParams({
    slot,
    clientType: terminal === "h5" ? "h5" : terminal,
    ...(terminal !== "h5" ? { os: terminal } : {}),
  });
  const channel = new URLSearchParams(location.search).get("channel");
  if (channel) query.set("channel", channel);
  return (await getJSON(`/ads/resolve?${query}`, signal)) as ResolvedAds | null;
}
export async function reportComponentAd(
  data: ResolvedAds,
  item: ResolvedAdItem,
  eventType: "impression" | "click",
) {
  const payload = {
    eventId: crypto.randomUUID(),
    eventType,
    requestId: data.requestId,
    planId: item.plan.id,
    adId: item.ad.id,
    slotId: data.slot.id,
    channel: data.channel,
  };
  const base = Capacitor.isNativePlatform() ? NATIVE_BASE : WEB_BASE;
  try {
    if (Capacitor.isNativePlatform())
      await CapacitorHttp.post({
        url: base.replace(/\/$/, "") + "/ads/events",
        headers: { "Content-Type": "application/json" },
        data: payload,
        connectTimeout: 12000,
        readTimeout: 12000,
      });
    else
      await fetch(base.replace(/\/$/, "") + "/ads/events", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        credentials: "omit",
        keepalive: true,
      });
  } catch {
    /* Statistics must not interrupt navigation. */
  }
}
